import { useState, useEffect } from 'react';
import { useSendSmsMutation, useSmsCertificateMutation } from 'views/utils/graphql/graphql';
import { useFormik } from 'formik';
import { ButtonProps, ButtonSize, ButtonType, FillType } from 'views/components/atoms/button';
import { SendSMSParam } from 'views/components/templates/sendSMS';
import { useUpdatePage } from './useUpdatePage';
import { useUpdateConfirm } from './useUpdateConfirm';
import { useToast } from 'views/components/atoms/toast/hook';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export enum PageStep {
  INITIAL,
  AUTH,
  UPDATE,
  CONFIRM,
}

export const useAccountInfo = () => {
  const [pageStep, setPageStep] = useState<PageStep>(PageStep.INITIAL);
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');

  const { toastProps, showToast } = useToast();
  const { onSendSMS } = useSendSMS(setProvisionalToken, pageStep, setPageStep, showToast, newPhoneNumber);
  const initislPageProps = useInitialPage(onSendSMS);
  const authPageProps = useAuthPage(provisionalToken, newPhoneNumber, setPageStep, showToast);
  const updatePageProps = useUpdatePage(pageStep, setPageStep, newPhoneNumber, setNewPhoneNumber, onSendSMS);

  const updateConfirmProps = useUpdateConfirm(setPageStep, updatePageProps.setSelected, setNewPhoneNumber, showToast);

  useEffect(() => {
    setPageStep(PageStep.INITIAL);
    setProvisionalToken('');
    setNewPhoneNumber('');
  }, []);

  return {
    pageStep,
    initislPageProps,
    authPageProps: {
      ...authPageProps,
      onSendSMS,
    },
    updatePageProps,
    updateConfirmProps,
    toastProps,
  };
};

//認証コード送信
const useSendSMS = (
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  pageStep: PageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  onShowToast: (label: string, messageLevel: ToastMessageLevel) => void,
  newPhoneNumber: string,
) => {
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const isNotNewPhoneNumber = newPhoneNumber === '';
        const res = await sendSMS({
          input: {
            Type: isNotNewPhoneNumber ? 2 : 4,
            PhoneNumber: isNotNewPhoneNumber ? undefined : newPhoneNumber,
          },
        });
        if (res.data && res.data.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          setPageStep(PageStep.AUTH);
          if (pageStep === PageStep.AUTH) {
            onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
          }
        }
      },
    },
    [sendSMS, setProvisionalToken, pageStep, setPageStep, onShowToast, newPhoneNumber],
  );

  return {
    onSendSMS,
  };
};

//初期ページ
const useInitialPage = (onSendSMS: () => Promise<void>) => {
  const sendSMSBtnProps: ButtonProps = {
    label: '認証コードを送信',
    type: ButtonType.Button,
    fillType: FillType.primaryFill,
    buttonSize: ButtonSize.sizeM,
    onClick: async () => await onSendSMS(),
  };
  return {
    sendSMSBtnProps,
  };
};

//認証ページ
const useAuthPage = (
  provisionalToken: string,
  newPhoneNumber: string,
  setPageStep: React.Dispatch<React.SetStateAction<PageStep>>,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();
  const isNotNewPhoneNumber = newPhoneNumber === '';

  const last4Digits = sessionStorage.getItem('last_4_digits') || '';
  const phoneNumber = isNotNewPhoneNumber ? last4Digits : newPhoneNumber.slice(-4);

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await smsCertificate({
          input: {
            Type: 2,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data?.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.SMSCertificate?.Code === 0) {
          isNotNewPhoneNumber ? setPageStep(PageStep.UPDATE) : setPageStep(PageStep.CONFIRM);
        }
      },
      waitMilliSecondsForHideLoading: 0,
    },
    [provisionalToken, showToast, isNotNewPhoneNumber, setPageStep],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  return { formik, phoneNumber };
};
